/**
 * Selection bookmarks.
 *
 * A DOM position is stored as a single linear offset over the editable root
 * rather than as a node reference, so a bookmark survives the tree being
 * rewritten underneath it -- which is exactly what normalization, paste and
 * undo do. Text contributes its length, a `<br>` contributes one, and every
 * block contributes one more when it closes, so the end of one paragraph and
 * the start of the next never collapse onto the same offset.
 */

/**
 * Only leaf blocks contribute a break. A `<ul>` must not, or converting two
 * paragraphs into a two-item list would add a third break and shift every
 * offset after it -- which shows up as the caret jumping on a list toggle.
 */
const BREAK_TAGS: ReadonlySet<string> = new Set(['P', 'LI']);

export interface Bookmark {
  readonly anchor: number;
  readonly focus: number;
}

export interface DomPoint {
  readonly node: Node;
  readonly offset: number;
}

function isBreakElement(node: Node): boolean {
  return node.nodeType === Node.ELEMENT_NODE && BREAK_TAGS.has(node.nodeName);
}

/** Converts a DOM position into a linear offset relative to `root`. */
export function toOffset(root: HTMLElement, container: Node, containerOffset: number): number {
  let position = 0;
  let found = -1;

  const visit = (node: Node): boolean => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node as Text;
      if (node === container) {
        found = position + Math.min(containerOffset, text.data.length);
        return true;
      }
      position += text.data.length;
      return false;
    }

    if (node.nodeName === 'BR') {
      if (node === container) {
        found = position;
        return true;
      }
      position += 1;
      return false;
    }

    if (node.nodeType !== Node.ELEMENT_NODE && node.nodeType !== Node.DOCUMENT_FRAGMENT_NODE) {
      return false;
    }

    const children = Array.from(node.childNodes);

    if (node === container) {
      const stopAt = Math.min(containerOffset, children.length);
      for (let i = 0; i < stopAt; i++) {
        visit(children[i]!);
      }
      found = position;
      return true;
    }

    for (const child of children) {
      if (visit(child)) {
        return true;
      }
    }

    if (node !== root && isBreakElement(node)) {
      position += 1;
    }
    return false;
  };

  visit(root);
  return found >= 0 ? found : position;
}

/** Converts a linear offset back into the closest surviving DOM position. */
export function fromOffset(root: HTMLElement, target: number): DomPoint {
  let position = 0;
  let result: DomPoint | null = null;

  const visit = (node: Node): boolean => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node as Text;
      if (target <= position + text.data.length) {
        result = { node, offset: Math.max(0, target - position) };
        return true;
      }
      position += text.data.length;
      return false;
    }

    if (node.nodeName === 'BR') {
      if (target <= position) {
        const parent = node.parentNode;
        result = parent
          ? { node: parent, offset: Array.from(parent.childNodes).indexOf(node as ChildNode) }
          : { node, offset: 0 };
        return true;
      }
      position += 1;
      return false;
    }

    if (node.nodeType !== Node.ELEMENT_NODE && node.nodeType !== Node.DOCUMENT_FRAGMENT_NODE) {
      return false;
    }

    for (const child of Array.from(node.childNodes)) {
      if (visit(child)) {
        return true;
      }
    }

    if (node !== root && isBreakElement(node)) {
      position += 1;
      if (target < position) {
        result = { node, offset: node.childNodes.length };
        return true;
      }
    }
    return false;
  };

  visit(root);
  return result ?? { node: root, offset: root.childNodes.length };
}

/** Reads the current selection as a bookmark, or `null` when it is elsewhere. */
export function save(root: HTMLElement): Bookmark | null {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return null;
  }

  const { anchorNode, anchorOffset, focusNode, focusOffset } = selection;
  if (!anchorNode || !focusNode || !root.contains(anchorNode) || !root.contains(focusNode)) {
    return null;
  }

  return {
    anchor: toOffset(root, anchorNode, anchorOffset),
    focus: toOffset(root, focusNode, focusOffset),
  };
}

/** Restores a bookmark, clamping to the nearest position that still exists. */
export function restore(root: HTMLElement, bookmark: Bookmark | null): void {
  if (!bookmark) {
    return;
  }

  const selection = window.getSelection();
  if (!selection) {
    return;
  }

  const anchor = fromOffset(root, bookmark.anchor);
  const focus = fromOffset(root, bookmark.focus);

  try {
    selection.setBaseAndExtent(anchor.node, anchor.offset, focus.node, focus.offset);
  } catch {
    // The tree changed more than the clamp could absorb; put the caret at the end.
    const fallback = fromOffset(root, Number.MAX_SAFE_INTEGER);
    const range = document.createRange();
    range.setStart(fallback.node, fallback.offset);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
  }
}

/** A collapsed bookmark at a single offset. */
export function collapsed(offset: number): Bookmark {
  return { anchor: offset, focus: offset };
}
